// One stroke-icon set at a single weight, tinted from the token palette —
// replaces emoji standing in as an icon system (they render differently per
// platform and break the type/weight rhythm). 20px grid, 1.6 stroke, inherits
// currentColor so a tile/chip controls its own tint.

export type IconName =
  | 'questions'
  | 'estimate'
  | 'board'
  | 'run'
  | 'doc'
  | 'models'
  | 'accuracy'
  | 'takeoff'
  | 'clock'
  | 'people'
  | 'check'
  | 'placement'
  | 'trade'
  | 'software'
  | 'worker'

const PATHS: Record<IconName, JSX.Element> = {
  // ── The three audiences. These exist because the switcher's three cards were
  // all trade-agnostic except Code, so the only world the page NAMED was
  // software — a contractor scanning them had no reason to think the product
  // was for them. An icon reads before the blurb does.
  //
  // post-and-beam — physical work. Deliberately the pergola elevation in
  // miniature: two posts, a beam, rafters over it. It is the Build view's own
  // hero drawing, so the card and the schematic it leads to agree.
  trade: (
    <>
      <path d="M2.6 5.6h14.8" />
      <path d="M5 5.6v10.8M15 5.6v10.8" />
      <path d="M5.6 3.4v2.2M8.5 3.4v2.2M11.5 3.4v2.2M14.4 3.4v2.2" opacity=".65" />
      <path d="M3.4 16.4h3.2M13.4 16.4h3.2" />
    </>
  ),
  // angle brackets — software work. The one glyph nobody has to be taught.
  software: (
    <>
      <path d="M7.2 6.4 3.4 10l3.8 3.6" />
      <path d="M12.8 6.4 16.6 10l-3.8 3.6" />
      <path d="M11.2 4.4 8.8 15.6" opacity=".65" />
    </>
  ),
  // one figure, centred and nothing else. The first cut put a wrench beside a
  // small off-centre head: at 19px the tool was three overlapping strokes in
  // four pixels — unreadable — and the head sat high-left because the figure
  // had been shoved aside to make room for it. `people` is two figures and
  // means "work that needs a person"; this is THE person, so it gets the whole
  // 20px grid and no props.
  worker: (
    <>
      <circle cx="10" cy="6.4" r="2.7" />
      <path d="M4.6 16.4a5.4 5.4 0 0 1 10.8 0" />
    </>
  ),
  // a panel with its right column filled — WHERE the assistant sits. The
  // node-graph glyph that was here read as a share icon.
  placement: (
    <>
      <rect x="2.8" y="4" width="14.4" height="12" rx="1.8" />
      <path d="M12.4 4v12" />
      <path d="M12.4 6.2h4.8M12.4 8.6h4.8M12.4 11h4.8M12.4 13.4h4.8" opacity=".55" />
    </>
  ),
  // clock — hours on site
  clock: (
    <>
      <circle cx="10" cy="10" r="7.2" />
      <path d="M10 5.9V10l2.9 1.8" />
    </>
  ),
  // two figures — the work that needs a person
  people: (
    <>
      <circle cx="7.6" cy="7.4" r="2.6" />
      <path d="M3.2 16.4a4.6 4.6 0 0 1 8.8 0" />
      <path d="M13.2 5.2a2.6 2.6 0 0 1 0 4.9" />
      <path d="M14.4 12.4a4.6 4.6 0 0 1 2.6 4" />
    </>
  ),
  // tick — a stage that is behind you
  check: <path d="M4.8 10.4 8.4 14l6.8-8" />,
  // question in a circle — something is being asked of you
  questions: (
    <>
      <circle cx="10" cy="10" r="7.2" />
      <path d="M8.1 7.9a1.95 1.95 0 1 1 2.6 1.85c-.5.2-.8.66-.8 1.2v.5" />
      <path d="M10 14.2h.01" />
    </>
  ),
  // price tag — the money artifact
  estimate: (
    <>
      <path d="M10.6 3.2H16.2a.6.6 0 0 1 .6.6v5.6a1 1 0 0 1-.3.7l-6.4 6.4a1 1 0 0 1-1.4 0l-5-5a1 1 0 0 1 0-1.4l6.4-6.4a1 1 0 0 1 .5-.5Z" />
      <circle cx="13.6" cy="6.4" r="1.05" />
    </>
  ),
  // stacked lanes — the task board
  board: (
    <>
      <rect x="3" y="4" width="14" height="12" rx="1.6" />
      <path d="M3 8h14" />
      <path d="M8.4 8v8" />
    </>
  ),
  // play in a ring — finalize & run
  run: (
    <>
      <circle cx="10" cy="10" r="7.2" />
      <path d="M8.4 7.2 13.2 10l-4.8 2.8V7.2Z" />
    </>
  ),
  // page with lines — the design doc
  doc: (
    <>
      <path d="M5.4 3.2h6.2l3.8 3.8v9.8a1 1 0 0 1-1 1H5.4a1 1 0 0 1-1-1V4.2a1 1 0 0 1 1-1Z" />
      <path d="M11.4 3.4V7h3.6" />
      <path d="M7.2 11h5.6M7.2 13.8h4" />
    </>
  ),
  // robot head — which model runs each step. Second time the node-graph
  // glyph read as a share icon (see `placement` above); this artifact is
  // ABOUT the machines, so it wears the machine's face.
  models: (
    <>
      <path d="M10 4.6V3" />
      <circle cx="10" cy="2.4" r="0.6" fill="currentColor" stroke="none" />
      <rect x="4.4" y="4.6" width="11.2" height="9.6" rx="2" />
      <path d="M7.7 8.3v1.8M12.3 8.3v1.8" />
      <path d="M7.6 11.7h4.8" opacity=".7" />
      <path d="M4.4 8.4H2.7M15.6 8.4h1.7" />
      <path d="M7.2 14.2v2M12.8 14.2v2" opacity=".55" />
    </>
  ),
  // clipboard list — what to buy
  takeoff: (
    <>
      <path d="M7.4 4.2H5.8a1 1 0 0 0-1 1v10.6a1 1 0 0 0 1 1h8.4a1 1 0 0 0 1-1V5.2a1 1 0 0 0-1-1h-1.6" />
      <rect x="7.4" y="2.9" width="5.2" height="2.6" rx=".7" />
      <path d="M7.8 9.4h4.4M7.8 12.2h3" />
    </>
  ),
  // target — estimate vs. actual
  accuracy: (
    <>
      <circle cx="10" cy="10" r="7.2" />
      <circle cx="10" cy="10" r="3.4" />
      <circle cx="10" cy="10" r="0.6" fill="currentColor" stroke="none" />
    </>
  ),
}

export function Icon({ name, size = 18 }: { name: IconName; size?: number }) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  )
}
